<template>
  <div class="meeting-menu-container">
    <div class="meeting-menu-header">
      <h2>Google Meet Integration</h2>
      <button @click="$emit('close')" class="close-button">×</button>
    </div>
    <div class="meeting-menu-content">
      <!-- Google OAuth Login Section -->
      <div class="section">
        <h3>Authentication</h3>
        <div class="form-group">
          <label for="client-id">Client ID</label>
          <input 
            id="client-id" 
            v-model="clientId" 
            type="text"
            placeholder="Enter Google Cloud Client ID"
          />
        </div>
        <button @click="loginButtonClick" class="btn" :class="isLoggedIn ? 'btn-secondary' : 'btn-primary'">
          {{ isLoggedIn ? `Logout ${currentUserEmail}` : 'Login with Google' }}
        </button>
        
        <!-- Account Verification Warning -->
        <div v-if="currentUserEmail && !currentUserEmail.includes('@ituri.ai')" class="warning-box">
          <strong>⚠️ Account Mismatch Warning:</strong>
          <p>You are currently logged in with: <strong>{{ currentUserEmail }}</strong></p>
          <p>To join meetings created with your @ituri.ai account, you need to:</p>
          <ol>
            <li>Click "Logout" above</li>
            <li>Click "Login with Google" again</li>
            <li>Select your @ituri.ai account when prompted</li>
          </ol>
        </div>
      </div>

      <!-- Meeting Configuration Section -->
      <div class="section">
        <h3>Meeting Setup</h3>
        <div class="status-indicators">
          <span class="status-indicator" :class="{ active: isLoggedIn }">
            🔐 {{ isLoggedIn ? 'Logged In' : 'Not Logged In' }}
          </span>
          <span class="status-indicator" :class="{ active: hasMeetingCode }">
            📝 {{ hasMeetingCode ? 'Meeting Code Set' : 'No Meeting Code' }}
          </span>
          <span class="status-indicator" :class="{ active: client }">
            🔗 {{ client ? 'Client Connected' : 'No Client' }}
          </span>
          <span class="status-indicator" :class="{ active: disconnectionComplete }" v-if="!disconnectionComplete && isLoggedIn && hasMeetingCode">
            ⏳ Waiting for Disconnect
          </span>
        </div>
        <div class="form-group">
          <label for="meeting-code">Meeting Code</label>
          <div class="meeting-code-input-group">
            <input 
              id="meeting-code" 
              v-model="meetingCode" 
              type="text"
              placeholder="Enter meeting code or paste full meeting URL"
              @paste="handleMeetingCodePaste"
            />
            <button @click="createSpace" class="btn btn-secondary" :disabled="!canCreateSpace">Create New</button>
          </div>
        </div>
        <div class="form-group" v-if="meetingCode">
          <label>Meeting Link</label>
          <div class="meeting-link">
            <a :href="`https://meet.google.com/${meetingCode}`" target="_blank" class="meeting-link-text">
              https://meet.google.com/{{ meetingCode }}
            </a>
            <button @click="copyMeetingLink" class="btn btn-secondary copy-btn">Copy</button>
          </div>
        </div>
        <div class="form-group">
          <label for="video-stream">Number of video streams: {{ videoStreamCount }}</label>
          <input 
            id="video-stream" 
            v-model="videoStreamCount" 
            type="range" 
            min="0" 
            max="3" 
            class="range-input"
          />
        </div>
        <div class="form-group">
          <label>
            <input 
              id="audio-stream" 
              v-model="enableAudio" 
              type="checkbox"
            />
            Enable Audio
          </label>
        </div>
        <div class="join-status-container">
          <button @click="joinLeaveButtonClick" class="btn" :class="sessionStatus === 'JOINED' ? 'btn-danger' : 'btn-primary'" :disabled="sessionStatus === 'JOINED' ? isLeavingMeeting : !canJoinMeeting">
            {{ sessionStatus === 'JOINED' ? (isLeavingMeeting ? 'Leaving...' : 'Leave Meeting') : (disconnectionComplete ? 'Join Meeting' : 'Waiting for disconnect...') }}
          </button>
          <span class="session-status">Session Status: {{ sessionStatus }}</span>
        </div>
        
        <!-- Help text for disabled buttons -->
        <div v-if="!canJoinMeeting && sessionStatus !== 'JOINED'" class="help-text">
          <small>
            💡 <strong>Join Meeting</strong> requires: 
            <span v-if="!isLoggedIn">Login with Google</span>
            <span v-if="!isLoggedIn && !hasMeetingCode"> and </span>
            <span v-if="!hasMeetingCode">Meeting Code</span>
            <span v-if="!client && isLoggedIn && hasMeetingCode && disconnectionComplete"> (Client will be created automatically)</span>
            <span v-if="!disconnectionComplete && isLoggedIn && hasMeetingCode"> (Waiting for previous connection to fully disconnect)</span>
          </small>
        </div>
      </div>

      <!-- Meeting Controls Section -->
      <div class="section">
        <h3>Meeting Management</h3>

        <button @click="refreshParticipants" class="btn btn-secondary" :disabled="!client">Refresh Participants</button>
        <button @click="refreshParticipantMapping" class="btn btn-secondary" :disabled="!client">Refresh Audio Mapping</button>
        <button @click="testApiConnectivity" class="btn btn-secondary" :disabled="!isLoggedIn">Test API Connectivity</button>
        <button @click="resetDisconnectionState" class="btn btn-secondary" :disabled="disconnectionComplete">Force Allow Rejoin</button>
        <button @click="testAudioProcessing" class="btn btn-secondary">Test Audio Processing</button>
      </div>

      <!-- Audio Streaming Section -->
      <div class="section audio-section">
        <h3>Audio Stream Processing</h3>
        <p><strong>Real-time audio capture and server streaming</strong></p>

        <div class="form-group">
          <label for="server-url">Server WebSocket URL:</label>
          <input 
            id="server-url" 
            v-model="serverUrl" 
            type="text"
            placeholder="ws://localhost:8081"
          />
        </div>

        <div class="button-group">
                  <button @click="startStreamingToServer" class="btn btn-success" :disabled="!canStartStreaming">
          Start Streaming
        </button>
        <button @click="stopStreamingToServer" class="btn btn-danger" :disabled="!canStopStreaming">
          Stop Streaming
        </button>
        </div>

        <p class="status">Streaming Status: {{ streamingStatus }}</p>

        <div class="info-box">
          <h4>Audio Processing Info:</h4>
          <ul>
            <li>Audio streams are automatically captured when you join a meeting</li>
            <li>Real-time audio data is processed using Web Audio API</li>
            <li>Audio data is sent to server via WebSocket in base64 format</li>
            <li>Each audio chunk includes: streamId, timestamp, sampleRate, audio data, and participant metadata</li>
            <li>Participant metadata includes: display name, user ID, and participant type (emails not available)</li>
            <li>Audio levels are logged to console when significant audio is detected</li>
            <li><strong>Note:</strong> You can start streaming before joining a meeting - it will begin sending audio once you join</li>
          </ul>
        </div>
      </div>

      <!-- Participant Management Section -->
      <div class="section">
        <h3>Invite Participants</h3>
        <div class="form-group">
          <label for="participant-email">Email Address:</label>
          <input 
            id="participant-email" 
            v-model="participantEmail" 
            type="email" 
            placeholder="Enter email address"
          />
        </div>
        <button @click="inviteParticipant" class="btn btn-primary" :disabled="!canInviteParticipant">Invite Participant</button>

        <h4>Default Participants</h4>
        <div class="info-box" v-if="defaultParticipants.length > 0">
          <p><strong>Default participants will be automatically invited when creating a new meeting:</strong></p>
          <ul>
            <li v-for="participant in defaultParticipants" :key="participant">{{ participant }}</li>
          </ul>
        </div>
        <div class="info-box" v-else>
          <p><strong>Default participants will be set after Google login</strong></p>
        </div>

        <h4>Current Participants</h4>
        <div class="participants-list" v-html="participantsList"></div>
      </div>

      <!-- Results Section -->
      <div class="section" v-if="spaceResult">
        <h3>Space Creation Result</h3>
        <pre class="result-box">{{ spaceResult }}</pre>
      </div>

      <!-- Video Elements (hidden but needed for streams) -->
      <div class="video-container" style="display: none;">
        <video id="video-1" autoplay></video>
        <video id="video-2" autoplay></video>
        <video id="video-3" autoplay></video>
        <video id="video-4" autoplay></video>
        <video id="video-5" autoplay></video>
        <video id="video-6" autoplay></video>
        <audio id="audio-1" autoplay></audio>
        <audio id="audio-2" autoplay></audio>
        <audio id="audio-3" autoplay></audio>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineEmits, computed } from 'vue';
import { MeetMediaApiClientImpl } from '../meetings/meetmediaapiclient_impl';
import { MeetConnectionState } from '../types/enums';
import { MeetStreamTrack, MeetMediaClientRequiredConfiguration } from '../types/mediatypes';
import { MeetSessionStatus } from '../types/meetmediaapiclient';

// Calculate allocation size based on format
function getBytesPerSample(format: string): number {
  switch (format) {
    case 'f32-planar':
    case 'f32-interleaved':
      return 4; // 32-bit float
    case 's16-planar':
    case 's16-interleaved':
      return 2; // 16-bit signed integer
    default:
      console.warn(`Unknown format: ${format}, assuming 4 bytes per sample`);
      return 4;
  }
}

const emit = defineEmits<{
  (e: 'close'): void;
}>();

// Reactive data
const clientId = ref('1080270416070-3tukbhds4s7nvgroil32vhl6lt7s48le.apps.googleusercontent.com');
const meetingCode = ref('');
const videoStreamCount = ref(0);
const enableAudio = ref(true);
const sessionStatus = ref('New');
const serverUrl = ref('ws://localhost:8081');
const streamingStatus = ref('Not streaming');
const participantEmail = ref('');
const participantsList = ref('No participants yet');
const spaceResult = ref('');
const defaultParticipants = ref<string[]>([]);
const currentUserEmail = ref('');
const tokenResponse = ref<any>(null);
const client = ref<MeetMediaApiClientImpl | null>(null);
const isLeavingMeeting = ref(false);
const disconnectionComplete = ref(false);


// Computed properties for button states
const isLoggedIn = computed(() => !!tokenResponse.value?.access_token && !!currentUserEmail.value);
const hasMeetingCode = computed(() => !!meetingCode.value?.trim());
const canCreateSpace = computed(() => isLoggedIn.value);
const canJoinMeeting = computed(() => isLoggedIn.value && hasMeetingCode.value && disconnectionComplete.value);
const canInviteParticipant = computed(() => isLoggedIn.value && hasMeetingCode.value);
const canStartStreaming = computed(() => isLoggedIn.value && !isStreamingToServer.value);
const canStopStreaming = computed(() => isStreamingToServer.value);

// Global variables (adapted from script.ts)
const isStreamingToServer = ref(false);
let serverWebSocket: WebSocket | null = null;



// Map to track audio processing for each stream
const audioStreamProcessors = new Map<string, {
  audioContext?: AudioContext;
  audioProcessor?: ScriptProcessorNode;
  source?: MediaStreamAudioSourceNode;
  audioElement?: HTMLAudioElement;
}>();

const VIDEO_IDS = [1, 2, 3, 4, 5, 6];
const AUDIO_IDS = [1, 2, 3];

let availableVideoIds = [...VIDEO_IDS];
let availableAudioIds = [...AUDIO_IDS];
const trackIdToElementId = new Map<string, number>();

// Functions adapted from script.ts
async function handleSessionChange(status: MeetSessionStatus) {
  let statusString;
  switch (status.connectionState) {
    case MeetConnectionState.WAITING:
      statusString = 'WAITING';
      break;
    case MeetConnectionState.JOINED:
      statusString = 'JOINED';
      console.log('Session state changed to JOINED - checking participants...');
      
      // Reset disconnection state when joining
      disconnectionComplete.value = false;
      
      // Only apply layout if video is enabled
      if (client.value && videoStreamCount.value > 0) {
        const mediaLayout = client.value.createMediaLayout({ width: 500, height: 500 });
        const response = await client.value.applyLayout([{ mediaLayout }]);
        console.log(response);
      }

      // Start audio stream processing when joined
      startAudioStreamProcessing();
      
      // Refresh participant list when joined
      setTimeout(() => {
        if (client.value) {
          const joinedParticipants = client.value.participants.get();
          console.log('Participants when session joined:', joinedParticipants.length);
          console.log('Participants data when session joined:', joinedParticipants);
          displayParticipants();
        }
      }, 1000);
      break;
    case MeetConnectionState.DISCONNECTED:
      statusString = 'DISCONNECTED';
      console.log('Session state changed to DISCONNECTED');
      
      // Stop audio stream processing when disconnected
      stopAudioStreamProcessing();
      
      // Mark disconnection as complete after a short delay to ensure all cleanup is done
      setTimeout(() => {
        disconnectionComplete.value = true;
        console.log('Disconnection marked as complete - can rejoin now');
      }, 2000); // 2 second delay to ensure all cleanup is complete
      break;
    default:
      statusString = 'UNKNOWN';
      break;
  }
  sessionStatus.value = statusString;
}

function handleStreamChange(meetStreamTracks: MeetStreamTrack[]) {
  // We create local sets of ids so that we don't have to add back ids when
  // tracks are removed.
  const localAvailableVideoIds = new Set(VIDEO_IDS);
  const localAvailableAudioIds = new Set(AUDIO_IDS);
  
  meetStreamTracks.forEach((meetStreamTrack: MeetStreamTrack) => {
    if (meetStreamTrack.mediaStreamTrack.kind === 'video') {
      const elementId = trackIdToElementId.get(meetStreamTrack.mediaStreamTrack.id);
      if (elementId) {
        // If a track is already in the element then we remove it from the local
        // ids and continue.
        localAvailableVideoIds.delete(elementId);
        return;
      }
      
      // If this is a new track, then we create a MediaStream and add it to a
      // video element.
      const mediaStream = new MediaStream();
      mediaStream.addTrack(meetStreamTrack.mediaStreamTrack);

      // Update id collections. We do expect to run out of available ids, but
      // reassign to a valid id (1) in case we do.
      const videoId = availableVideoIds.pop() ?? 1;
      localAvailableVideoIds.delete(videoId);

      // Retrieve available video element and assign media stream to it.
      const videoIdString = `video-${videoId}`;
      const videoElement = document.getElementById(videoIdString) as HTMLVideoElement;
      if (videoElement) {
        videoElement.srcObject = mediaStream;
      }
      trackIdToElementId.set(meetStreamTrack.mediaStreamTrack.id, videoId);
    } else if (meetStreamTrack.mediaStreamTrack.kind === 'audio') {
      const elementId = trackIdToElementId.get(meetStreamTrack.mediaStreamTrack.id);
      if (elementId) {
        // If a track is already in the element then we remove it from the local
        // ids and continue.
        localAvailableAudioIds.delete(elementId);
        return;
      }

      // If this is a new track, then we create a MediaStream and add it to a
      // audio element.
      const mediaStream = new MediaStream();
      mediaStream.addTrack(meetStreamTrack.mediaStreamTrack);

      // Update id collections. We do expect to run out of available ids, but
      // reassign to a valid id (1) in case we do.
      const audioId = availableAudioIds.pop() ?? 1;
      localAvailableAudioIds.delete(audioId);

      // Retrieve available audio element and assign media stream to it.
      const audioIdString = `audio-${audioId}`;
      const audioElement = document.getElementById(audioIdString) as HTMLAudioElement;
      if (audioElement) {
        audioElement.srcObject = mediaStream;
      }
      trackIdToElementId.set(meetStreamTrack.mediaStreamTrack.id, audioId);

      // Automatically process new audio streams for real-time processing
      // Only process when session is JOINED to avoid processing during WAITING state
      console.log(`New audio stream detected: ${audioIdString} (session status: ${sessionStatus.value})`);
      if (sessionStatus.value === 'JOINED') {
      setTimeout(() => {
        if (audioElement && audioElement.srcObject) {
          processAudioStream(audioElement.srcObject as MediaStream, audioIdString);
        }
      }, 100); // Small delay to ensure the stream is properly set
      } else {
        console.log(`Skipping audio processing for ${audioIdString} - session not yet joined`);
      }
    }
  });

  // Set local set of tracks to top level available id collections.
  availableVideoIds = [...localAvailableVideoIds];
  availableAudioIds = [...localAvailableAudioIds];
}

async function createClient(): Promise<void> {
  console.log('=== createClient function called ===');
  
  if (!tokenResponse.value) {
    console.log('No token response in createClient');
    throw new Error('You must login before creating the client');
  }
  
  if (!meetingCode.value) {
    console.log('No meeting code in createClient');
    throw new Error('Please enter a meeting code or create a space first');
  }
  
  const authToken = tokenResponse.value.access_token;
  console.log('Auth token exists:', !!authToken);
  console.log('Auth token length:', authToken?.length);
  
  // Create the actual MeetMediaApiClientImpl
  const config: MeetMediaClientRequiredConfiguration = {
    meetingSpaceId: 'spaces/' + meetingCode.value,
    numberOfVideoStreams: videoStreamCount.value,
    enableAudioStreams: enableAudio.value,
    accessToken: authToken
  };
  
  console.log('=== Configuration Debug ===');
  console.log('videoStreamCount.value:', videoStreamCount.value);
  console.log('enableAudio.value:', enableAudio.value);
  console.log('config.numberOfVideoStreams:', config.numberOfVideoStreams);
  console.log('config.enableAudioStreams:', config.enableAudioStreams);
  console.log('=== End Configuration Debug ===');

  // Print all configuration parameters at runtime
  console.log('=== Google Meet API Configuration Parameters ===');
  console.log('meetingSpaceId:', config.meetingSpaceId);
  console.log('numberOfVideoStreams:', config.numberOfVideoStreams);
  console.log('enableAudioStreams:', config.enableAudioStreams);
  console.log('accessToken exists:', !!config.accessToken);
  console.log('accessToken length:', config.accessToken?.length);
  console.log('accessToken preview:', config.accessToken ? `${config.accessToken.substring(0, 20)}...` : 'undefined');
  console.log('=== End Configuration Parameters ===');

  try {
    console.log('About to create MeetMediaApiClientImpl...');
    client.value = new MeetMediaApiClientImpl(config);
    console.log('MeetMediaApiClientImpl created successfully');

    // Set up subscriptions
    console.log('Setting up subscriptions...');
    client.value.sessionStatus.subscribe(handleSessionChange);
    client.value.meetStreamTracks.subscribe(handleStreamChange);
    client.value.participants.subscribe(() => {
      console.log('Participants subscription triggered - updating display');
      displayParticipants();
    });
    
    // Initial participant check
    console.log('Initial participant check after client creation:');
    const initialParticipants = client.value.participants.get();
    console.log('Initial participants count:', initialParticipants.length);
    console.log('Initial participants data:', initialParticipants);

    console.log('Media API Client created successfully.');
  } catch (error) {
    console.error('Error creating Meet client:', error);
    console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace');
    throw new Error('Error creating Meet client: ' + error);
  }
}

async function joinMeeting() {
  console.log('=== joinMeeting function called ===');
  console.log('tokenResponse.value exists:', !!tokenResponse.value);
  console.log('meetingCode.value:', meetingCode.value);
  console.log('canJoinMeeting.value:', canJoinMeeting.value);
  
  // Validate prerequisites
  if (!tokenResponse.value) {
    console.log('No token response - showing alert');
    alert('You must login before joining a meeting');
    return;
  }

  if (!meetingCode.value || meetingCode.value.trim() === '') {
    console.log('No meeting code - showing alert');
    alert('Please enter a valid meeting code');
    return;
  }
  
  // Check if access token is still valid
  if (!tokenResponse.value?.access_token) {
    console.log('No access token - showing alert');
    alert('Access token is missing. Please login again.');
    return;
  }

  console.log('All prerequisites met, proceeding with join...');

  // Create client if it doesn't exist
  if (!client.value) {
    console.log('No client found. Creating client automatically...');
    try {
      await createClient();
      console.log('Client created successfully');
    } catch (error) {
      console.error('Failed to create client:', error);
      alert('Failed to create client: ' + error);
      return;
    }
  } else {
    console.log('Using existing client for join attempt');
  }

  // Ensure client exists after creation
  if (!client.value) {
    console.log('Client still null after creation attempt');
    alert('Failed to create client. Please try again.');
    return;
  }

  // Add debugging information
  console.log('Attempting to join meeting...');
  console.log('Meeting Space ID:', 'spaces/' + meetingCode.value);
  console.log('Access Token exists:', !!tokenResponse.value?.access_token);
  console.log('Access Token length:', tokenResponse.value?.access_token?.length);
  console.log('Video streams:', videoStreamCount.value);
  console.log('Audio enabled:', enableAudio.value);
  
  // Check current participants (informational only)
  const participants = client.value.participants.get();
  console.log('Current participants before joining:', participants.length);
  console.log('Participants data:', participants);
  
  // Also check if the participants subscription is working
  console.log('Client participants object:', client.value.participants);

  try {
    console.log('About to call client.joinMeeting()...');
    const result = await client.value.joinMeeting();
    console.log('Join meeting result:', result);
    
    // Check participants after joining
    setTimeout(() => {
      const participantsAfterJoin = client.value!.participants.get();
      console.log('Participants after joining:', participantsAfterJoin.length);
      console.log('Participants data after joining:', participantsAfterJoin);
      if (participantsAfterJoin.length === 0) {
        console.log('No other participants in the meeting yet. You can invite participants or wait for them to join.');
      } else {
        console.log('Found participants in the meeting after joining!');
        displayParticipants(); // Update the UI with the new participant data
      }
    }, 2000); // Increased delay to allow more time for participant list to update
    
  } catch (error: any) {
    console.error('Error joining meeting:', error);
    console.error('Error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      cause: error?.cause
    });
    
    // Provide more specific error messages
    if (error?.message && error.message.includes('429')) {
      alert('Connection limit reached: Too many Media API connections for this meeting.\n\nThis usually happens when:\n1. You recently left the meeting and the connection hasn\'t been fully cleaned up yet\n2. Multiple instances are trying to connect to the same meeting\n\nPlease wait 30-60 seconds and try again, or try creating a new meeting.');
    } else if (error?.message && error.message.includes('Failed to fetch')) {
      alert('Network error: Unable to connect to Google Meet API. Please check:\n1. Your internet connection\n2. If the meeting code is valid\n3. If you have proper permissions for this meeting');
    } else if (error?.message && error.message.includes('403')) {
      alert('Permission denied (403): Unable to join the meeting.\n\nThis usually means:\n1. The meeting code is invalid or expired\n2. You don\'t have permission to join this meeting\n3. The meeting is full or has restricted access\n4. Your Google account doesn\'t have the required permissions\n\nTry:\n1. Creating a new meeting\n2. Checking the meeting code\n3. Using a different Google account');
    } else if (error?.message && error.message.includes('connectActiveConference')) {
      alert('Conference connection error: Unable to join the meeting. Please verify:\n1. The meeting code is correct\n2. The meeting is still active\n3. You have permission to join this meeting');
    } else {
      alert('Error joining meeting: ' + (error?.message || 'Unknown error'));
    }
    
    // Don't re-throw the error - it's already handled with the alert
    // Just return to let the UI handle the error state gracefully
    return;
  }
}

async function leaveMeeting() {
  console.log('=== leaveMeeting function called ===');
  
  if (isLeavingMeeting.value) {
    console.log('Already leaving meeting, ignoring duplicate request');
    return;
  }
  
  isLeavingMeeting.value = true;
  
  try {
    if (client.value) {
      console.log('Calling client.leaveMeeting()...');
      
      // Wait for the leave operation to complete (this waits for Google's server acknowledgment)
      await client.value.leaveMeeting();
      console.log('Successfully left meeting - server acknowledged leave request');
      
      // The session status will change to DISCONNECTED, which will trigger handleSessionChange
      // and set disconnectionComplete.value = true after cleanup
      
      // Clean up the client to force a new connection on next join
      console.log('Cleaning up client...');
      client.value = null;
      
      // Reset session status
      sessionStatus.value = 'New';
      
      // Stop audio processing
      stopAudioStreamProcessing();
      
      console.log('Meeting cleanup completed - waiting for disconnection to be marked complete');
    } else {
      console.log('No client to leave meeting with');
      // If no client, we can immediately mark disconnection as complete
      disconnectionComplete.value = true;
    }
  } catch (error) {
    console.error('Error leaving meeting:', error);
    // Even if there's an error, clean up the client
    client.value = null;
    sessionStatus.value = 'New';
    stopAudioStreamProcessing();
    
    // Mark disconnection as complete even on error
    disconnectionComplete.value = true;
  } finally {
    isLeavingMeeting.value = false;
  }
}

async function createSpace() {
  // Get the access token from your login flow
  const accessToken = tokenResponse.value?.access_token;
  if (!accessToken) {
    alert('You must log in first!');
    return;
  }

  try {
    const response = await fetch('https://meet.googleapis.com/v2/spaces', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const error = await response.json();
      alert('Error: ' + JSON.stringify(error));
      spaceResult.value = 'Error: ' + JSON.stringify(error, null, 2);
      return;
    }

    const space = await response.json();
    spaceResult.value = JSON.stringify(space, null, 2);
    
    if (space.meetingCode) {
      meetingCode.value = space.meetingCode;
      await inviteDefaultParticipants(space.meetingCode, accessToken);
    }
  } catch (error) {
    console.error('Error creating space:', error);
    alert('Error creating space: ' + error);
  }
}

async function inviteDefaultParticipants(meetingCode: string, accessToken: string): Promise<void> {
  if (defaultParticipants.value.length === 0) {
    console.log('No default participants set, skipping invitation');
    return;
  }

  console.log('Inviting default participants:', defaultParticipants.value);

  for (const email of defaultParticipants.value) {
    try {
      // Create calendar event with meeting link
      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          summary: 'Google Meet Meeting',
          description: `Join the meeting: https://meet.google.com/${meetingCode}`,
          start: {
            dateTime: new Date().toISOString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          end: {
            dateTime: new Date(Date.now() + 3600000).toISOString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          attendees: [{ email }],
          conferenceData: {
            createRequest: {
              requestId: meetingCode,
              conferenceSolutionKey: { type: 'hangoutsMeet' }
            }
          }
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error(`Error inviting ${email}:`, error);
        continue;
      }

      const event = await response.json();
      console.log(`Invitation sent to ${email}:`, event);

    } catch (error) {
      console.error(`Error inviting ${email}:`, error);
    }
  }

  // Show success message
  spaceResult.value += '\n\nDefault participants invited:\n' + defaultParticipants.value.join('\n');
}

async function inviteParticipant() {
  if (!participantEmail.value) {
    alert('Please enter an email address');
    return;
  }

  if (!meetingCode.value) {
    alert('Please enter a meeting code first');
    return;
  }

  const accessToken = tokenResponse.value?.access_token;
  if (!accessToken) {
    alert('You must log in first!');
    return;
  }

  try {
    // Create calendar event with meeting link
    const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        summary: 'Google Meet Meeting',
        description: `Join the meeting: https://meet.google.com/${meetingCode.value}`,
        start: {
          dateTime: new Date().toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: new Date(Date.now() + 3600000).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        attendees: [{ email: participantEmail.value }],
        conferenceData: {
          createRequest: {
            requestId: meetingCode.value,
            conferenceSolutionKey: { type: 'hangoutsMeet' }
          }
        }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      alert('Error inviting participant: ' + JSON.stringify(error));
      return;
    }

    const event = await response.json();
    console.log(`Invitation sent to ${participantEmail.value}!`);
    console.log('Calendar event created:', event);
    participantEmail.value = '';

  } catch (error) {
    console.error('Error inviting participant:', error);
    alert('Error inviting participant: ' + error);
  }
}

async function testApiConnectivity() {
  console.log('Testing API connectivity...');
  if (!tokenResponse.value?.access_token) {
    alert('No access token available. Please login first.');
    return;
  }
  
  console.log('Access token length:', tokenResponse.value.access_token.length);
  console.log('Access token preview:', tokenResponse.value.access_token.substring(0, 20) + '...');
  
  try {
    // Test Google APIs connectivity (this endpoint supports CORS)
    // Note: We skip basic internet connectivity test to avoid CORS issues when running locally
    console.log('Testing Google APIs connectivity...');
    const googleTest = await fetch('https://www.googleapis.com/discovery/v1/apis', { method: 'GET' });
    console.log('Google APIs test status:', googleTest.status);
    
    if (!googleTest.ok) {
      alert('Google APIs connectivity test failed. This might be a firewall or network issue.');
      return;
    }
    
    console.log('Google APIs connectivity test successful');
    
    // Test Meet API specifically
    console.log('Testing Meet API connectivity...');
    
    // Try different Meet API endpoints to find the correct one
    // Based on Google's implementation, the correct endpoint is v2beta
    const meetApiEndpoints = [
      'https://meet.googleapis.com/v2beta/spaces',
      'https://meet.googleapis.com/v2/spaces',
      'https://meet.googleapis.com/v1/spaces',
      'https://meet.googleapis.com/v2beta1/spaces',
      'https://meet.googleapis.com/v1beta1/spaces'
    ];
    
    let response = null;
    let workingEndpoint = null;
    let spacesCreationResult = 'Not tested';
    
    for (const endpoint of meetApiEndpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        response = await fetch(endpoint, {
          method: 'GET',
                  headers: {
          'Authorization': `Bearer ${tokenResponse.value.access_token}`,
          'Content-Type': 'application/json'
        }
        });
        
        console.log(`Endpoint ${endpoint} status: ${response.status}`);
        
        if (response.status !== 404) {
          workingEndpoint = endpoint;
          break;
        }
              } catch (error: any) {
          console.log(`Endpoint ${endpoint} failed:`, error?.message || 'Unknown error');
        }
    }
    
    if (!workingEndpoint) {
      console.log('All GET endpoints failed, but continuing with POST test...');
    } else {
      console.log(`Using working endpoint: ${workingEndpoint}`);
    }
    
    console.log(`Using working endpoint: ${workingEndpoint}`);
    
    if (!response) {
      alert('No valid response received from any Meet API endpoint');
      return;
    }
    
    // Also test the spaces creation endpoint (different from the media API)
    console.log('Testing spaces creation endpoint...');
    try {
      const spacesResponse = await fetch('https://meet.googleapis.com/v2/spaces', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenResponse.value.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      console.log('Spaces creation test status:', spacesResponse.status);
      if (spacesResponse.ok) {
        console.log('Spaces creation endpoint works - this suggests the issue is with the media API specifically');
        spacesCreationResult = '✅ Working';
      } else {
        console.log('Spaces creation endpoint also fails - this suggests a broader API access issue');
        spacesCreationResult = `❌ Failed (${spacesResponse.status})`;
      }
    } catch (error: any) {
      console.log('Spaces creation test failed:', error?.message);
      spacesCreationResult = `❌ Error: ${error?.message}`;
    }
    
    // Final summary based on actual results
    console.log('=== API Connectivity Test Summary ===');
    console.log('Google APIs: ✅ Working (status: 200)');
    console.log(`Meet API GET endpoints: ${workingEndpoint ? '✅ Working' : '❌ All failed (404)'}`);
    if (workingEndpoint) {
      console.log(`Working endpoint: ${workingEndpoint}`);
    }
    console.log(`Spaces creation POST: ${spacesCreationResult}`);
    console.log('=== End Summary ===');
    
    if (!workingEndpoint) {
      alert('API Connectivity Test Results:\n\n✅ Google APIs: Working\n❌ Meet API GET endpoints: All failed (404)\n\nThis suggests:\n1. Your account may not have access to the Google Meet API\n2. The API may not be enabled for your account\n3. The original developer needs to verify your access permissions\n\nTry the spaces creation test above to see if POST endpoints work.');
      return;
    }
    
    console.log('Meet API test response status:', response.status);
    console.log('Meet API test response headers:', Object.fromEntries(response.headers.entries()));
    
    if (response.ok) {
      console.log('Meet API connectivity test successful');
    } else {
      const errorText = await response.text();
      console.error('Meet API test failed - Status:', response.status);
      console.error('Meet API test failed - Response:', errorText);
      
      let errorMessage = `Meet API test failed (Status: ${response.status})`;
      
      if (response.status === 401) {
        errorMessage += '\n\nThis indicates an authentication issue. Please:\n1. Log out and log back in\n2. Check if your Google account has access to Google Meet API';
      } else if (response.status === 403) {
        errorMessage += '\n\nThis indicates a permissions issue. Please:\n1. Check if your Google Cloud project has the Meet API enabled\n2. Verify the OAuth scopes include meetings permissions';
      } else if (response.status === 404) {
        errorMessage += '\n\nThis indicates the API endpoint is not found. Please check the API URL.';
      }
      
      alert(errorMessage);
    }
  } catch (error: any) {
    console.error('API connectivity test error:', error);
    console.error('Error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      cause: error?.cause
    });
    
    let errorMessage = 'API connectivity test error: ' + (error?.message || 'Unknown error');
    
    if (error?.message?.includes('Failed to fetch')) {
      errorMessage += '\n\nThis usually indicates:\n1. Network connectivity issues\n2. Firewall blocking the request\n3. CORS issues (if running locally)\n4. DNS resolution problems\n\nNote: CORS errors are common when running locally. The test will continue with the Google APIs test.';
    }
    
    alert(errorMessage);
  }
}



function refreshParticipants() {
  console.log('Manually refreshing participants...');
  if (!client.value) {
    console.log('No client available for refresh');
    return;
  }
  
  const participants = client.value.participants.get();
  console.log('Refreshed participants count:', participants.length);
  console.log('Refreshed participants data:', participants);
  displayParticipants();
}

function refreshParticipantMapping() {
  console.log('Manually refreshing participant mapping...');
  if (!client.value) {
    console.log('No client available for refresh');
    return;
  }
  
  // Get all audio elements and try to map them
  const audioElements = document.querySelectorAll('audio[id^="audio-"]');
  console.log(`Found ${audioElements.length} audio elements to map`);
  
  for (let i = 0; i < audioElements.length; i++) {
    const audioElement = audioElements[i] as HTMLAudioElement;
    if (audioElement.srcObject) {
      const streamId = audioElement.id;
      const participantInfo = getParticipantForAudioStream(streamId);
      if (participantInfo) {
        console.log(`Successfully mapped ${streamId} to participant: ${participantInfo.displayName}`);
      } else {
        console.log(`Could not map ${streamId} to any participant`);
      }
    }
  }
}

function displayParticipants() {
  if (!client.value) {
    participantsList.value = '<div class="participant-item">No client connected</div>';
    return;
  }

  const participants = client.value.participants.get();
  console.log('Displaying participants:', participants);
  
  if (participants.length === 0) {
    participantsList.value = '<div class="participant-item">No participants in meeting (you may be the only one)</div>';
    return;
  }

  const participantHtml = participants.map((participant: any) => {
    const displayName = participant.participant.signedInUser?.displayName ||
      participant.participant.anonymousUser?.displayName ||
      participant.participant.phoneUser?.displayName ||
      'Unknown';

    const type = participant.participant.signedInUser ? 'Signed In' :
      participant.participant.anonymousUser ? 'Anonymous' :
        participant.participant.phoneUser ? 'Phone' : 'Unknown';

    const userId = participant.participant.signedInUser?.user || 'No user ID';

    return `<div class="participant-item">
      <strong>${displayName}</strong> (${type})
      <br><small>${userId}</small>
    </div>`;
  }).join('');

  participantsList.value = participantHtml;
  

}

// Audio processing functions
function startAudioStreamProcessing() {
  console.log('Starting audio stream processing...');

  // Get all audio elements that have streams
  const audioElements = document.querySelectorAll('audio[id^="audio-"]');

  if (audioElements.length === 0) {
    console.log('No audio elements found, waiting for streams...');
    return;
  }

  // Process each audio element
  for (let i = 0; i < audioElements.length; i++) {
    const audioElement = audioElements[i] as HTMLAudioElement;
    if (audioElement.srcObject) {
      console.log(`Processing audio stream ${i + 1}`);
      processAudioStream(audioElement.srcObject as MediaStream, `audio-${i + 1}`);
    }
  }
  
  // Update streaming status if we're connected to server
  if (isStreamingToServer.value && serverWebSocket && serverWebSocket.readyState === WebSocket.OPEN) {
    streamingStatus.value = 'Streaming audio to server';
  }
}

function processAudioStream(mediaStream: MediaStream, streamId: string) {
  try {
    // Check if we already have an audio context for this stream
    if (audioStreamProcessors.has(streamId)) {
      console.log(`Audio processing already active for ${streamId}, skipping...`);
      return;
    }

    // Get the audio element that's actually playing the audio
    const audioElement = document.getElementById(streamId) as HTMLAudioElement;
    if (!audioElement) {
      console.log(`Audio element ${streamId} not found`);
      return;
    }

    // WORKING APPROACH: Use ScriptProcessorNode like the Google sample
    console.log(`[AUDIO_SETUP] Setting up ScriptProcessorNode for ${streamId}`);
    
    const audioTracks = mediaStream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.log(`[AUDIO_SETUP] No audio tracks found in ${streamId}`);
      return;
    }

    const audioTrack = audioTracks[0];
    console.log(`[AUDIO_SETUP] ${streamId} audio track:`, {
      id: audioTrack.id,
      enabled: audioTrack.enabled,
      muted: audioTrack.muted,
      readyState: audioTrack.readyState,
      kind: audioTrack.kind
    });

    // Create AudioContext for processing
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    console.log(`[AUDIO_DEBUG] ${streamId}: AudioContext sampleRate=${audioContext.sampleRate}Hz`);
    
    // Create media source from stream
    const source = audioContext.createMediaStreamSource(mediaStream);
    
    // Check the actual number of channels in the audio track
    const streamAudioTracks = mediaStream.getAudioTracks();
    const streamAudioTrack = streamAudioTracks[0];
    console.log(`[CHANNEL_DEBUG] ${streamId}: Audio track channels:`, {
      id: streamAudioTrack.id,
      enabled: streamAudioTrack.enabled,
      muted: streamAudioTrack.muted,
      readyState: streamAudioTrack.readyState,
      kind: streamAudioTrack.kind
    });
    
    // Create script processor for real-time audio processing
    // Use the actual number of channels from the source
    const inputChannels = source.channelCount;
    const outputChannels = 1; // Always output mono for WAV file
    console.log(`[CHANNEL_DEBUG] ${streamId}: Creating ScriptProcessor with inputChannels=${inputChannels}, outputChannels=${outputChannels}`);
    const audioProcessor = audioContext.createScriptProcessor(4096, inputChannels, outputChannels);
    
    // Log the first audio processing event to see actual channel counts
    let firstProcess = true;
    
    // Process audio data in real-time
    audioProcessor.onaudioprocess = (event) => {
      const inputBuffer = event.inputBuffer;
      const outputBuffer = event.outputBuffer;
      

      
          // Log the first audio processing event to see actual channel counts
    if (firstProcess) {
      console.log(`[CHANNEL_DEBUG] ${streamId}: FIRST PROCESS - inputChannels=${inputBuffer.numberOfChannels}, outputChannels=${outputBuffer.numberOfChannels}, samples=${inputBuffer.length}, sampleRate=${audioContext.sampleRate}Hz`);
      console.log(`[CHANNEL_DEBUG] ${streamId}: Expected buffer duration: ${(inputBuffer.length / audioContext.sampleRate * 1000).toFixed(2)}ms`);
      firstProcess = false;
    }
      
      // Get output data (always mono)
      const outputData = outputBuffer.getChannelData(0);
      
      // Handle multiple input channels by mixing them to mono
      if (inputBuffer.numberOfChannels === 1) {
        // Mono input - direct copy
        const inputData = inputBuffer.getChannelData(0);
        for (let i = 0; i < inputData.length; i++) {
          outputData[i] = inputData[i];
        }
      } else if (inputBuffer.numberOfChannels === 2) {
        // Stereo input - mix to mono
        const leftChannel = inputBuffer.getChannelData(0);
        const rightChannel = inputBuffer.getChannelData(1);
        for (let i = 0; i < leftChannel.length; i++) {
          outputData[i] = (leftChannel[i] + rightChannel[i]) / 2;
        }
      } else {
        // Multiple channels - mix all channels
        for (let i = 0; i < outputData.length; i++) {
          let sum = 0;
          for (let ch = 0; ch < inputBuffer.numberOfChannels; ch++) {
            sum += inputBuffer.getChannelData(ch)[i];
          }
          outputData[i] = sum / inputBuffer.numberOfChannels;
        }
      }
      
      // Use the output data for processing (it's now mono)
      const inputData = outputData;
      
          
      
      // Calculate RMS audio level
      const audioLevel = calculateAudioLevel(inputData);
      
      // Check for non-zero audio data
      let hasNonZeroData = false;
      for (let i = 0; i < inputData.length; i++) {
        if (inputData[i] !== 0) {
          hasNonZeroData = true;
          break;
        }
      }
      
                // Send audio data to server if streaming
      if (isStreamingToServer.value && serverWebSocket && serverWebSocket.readyState === WebSocket.OPEN) {
        // Log sample rate and buffer info occasionally
        if (Math.random() < 0.01) { // Log ~1% of the time
          console.log(`[AUDIO_PROCESSOR] ${streamId} - Sample rate: ${audioContext.sampleRate}Hz, buffer length: ${inputData.length}, expected duration: ${(inputData.length / audioContext.sampleRate * 1000).toFixed(2)}ms`);
        }
        sendAudioDataToServer(inputData, streamId, audioContext.sampleRate);
      }
      
      // Log audio activity (but limit frequency to avoid spam)
      if (hasNonZeroData && audioLevel > 0.01 && Math.random() < 0.05) { // Log ~5% of audio detections
        console.log(`[AUDIO_PROCESSOR] ${streamId} - Audio detected: level=${audioLevel.toFixed(6)}, samples=${inputData.length}`);
      }
    };
    
    // Connect the audio processing chain
    source.connect(audioProcessor);
    
    // Create a silent gain node to trigger processing without audio output
    const silentGain = audioContext.createGain();
    silentGain.gain.setValueAtTime(0, audioContext.currentTime); // Set volume to 0
    audioProcessor.connect(silentGain);
    silentGain.connect(audioContext.destination); // Connect to destination but silent
    
    console.log(`[AUDIO_SETUP] ScriptProcessorNode processing started for ${streamId}`);
    
    // Store the processor for cleanup
    audioStreamProcessors.set(streamId, {
      audioContext,
      audioProcessor,
      source,
      audioElement
    });
    
  } catch (error) {
    console.error('Error processing audio stream:', error);
  }
}

function calculateAudioLevel(audioData: Float32Array): number {
  // Calculate RMS (Root Mean Square) which is more accurate for audio levels
  let sum = 0;
  for (let i = 0; i < audioData.length; i++) {
    sum += audioData[i] * audioData[i];
  }
  return Math.sqrt(sum / audioData.length);
}

function getParticipantForAudioStream(streamId: string): any {
  if (!client.value) {
    return null;
  }

  // Get the specific audio element for this stream ID
  const audioElement = document.getElementById(streamId) as HTMLAudioElement;
  if (!audioElement || !audioElement.srcObject) {
    return null;
  }

  const mediaStream = audioElement.srcObject as MediaStream;
  const audioTracks = mediaStream.getAudioTracks();
  if (audioTracks.length === 0) {
    return null;
  }

  // Get the track ID from the audio element
  const trackId = audioTracks[0].id;

  // Get media entries and find the one with matching audio track
  const mediaEntries = client.value.mediaEntries.get();
  
  // Only log participant lookup attempts occasionally to reduce spam
  if (Math.random() < 0.1) { // Log ~10% of the time
    console.log(`Looking for participant for stream ${streamId} with track ID: ${trackId}`);
    console.log('Available media entries:', mediaEntries.length);
  }

  // Find the media entry that has this audio track
  for (const mediaEntry of mediaEntries) {
    const audioTrack = mediaEntry.audioMeetStreamTrack.get();
    if (audioTrack && audioTrack.mediaStreamTrack.id === trackId) {
      // Found the match! Get participant info
      const participant = mediaEntry.participant.get();
      if (participant) {
        const participantInfo = {
          id: participant.participant.signedInUser?.user || 
              participant.participant.anonymousUser?.displayName || 
              participant.participant.phoneUser?.displayName || 
              'Unknown',
          displayName: participant.participant.signedInUser?.displayName ||
                      participant.participant.anonymousUser?.displayName ||
                      participant.participant.phoneUser?.displayName ||
                      'Unknown',
          userId: participant.participant.signedInUser?.user || null,
          type: participant.participant.signedInUser ? 'Signed In' :
                participant.participant.anonymousUser ? 'Anonymous' :
                participant.participant.phoneUser ? 'Phone' : 'Unknown'
        };
        
        // Only log successful mappings occasionally to reduce spam
        if (Math.random() < 0.1) { // Log ~10% of the time
          console.log(`Mapped stream ${streamId} to participant:`, participantInfo);
        }
        return participantInfo;
      }
    }
  }

  // Only log "no participant found" occasionally to reduce spam
  if (Math.random() < 0.1) { // Log ~10% of the time
    console.log(`No participant found for stream ${streamId}`);
  }
  return null;
}

function sendAudioDataToServer(audioData: Float32Array, streamId: string, sampleRate: number) {
  try {
    // Convert Float32Array to s16le for transmission (50% smaller)
    const s16leBuffer = new ArrayBuffer(audioData.length * 2);
    const view = new DataView(s16leBuffer);
    let clippingDetected = false;
    let sum = 0;
    let sumSquares = 0;
    
    for (let i = 0; i < audioData.length; i++) {
      // Convert float32 (-1.0 to 1.0) to int16 (-32768 to 32767)
      const s16Value = Math.max(-32768, Math.min(32767, Math.round(audioData[i] * 32767)));
      view.setInt16(i * 2, s16Value, true); // little-endian
      
      // Check for clipping
      if (audioData[i] > 1.0 || audioData[i] < -1.0) {
        clippingDetected = true;
      }
      
      // Calculate audio level (average absolute value)
      sum += Math.abs(audioData[i]);
      
      // Calculate RMS (root mean square)
      sumSquares += audioData[i] * audioData[i];
    }

    const s16leAudio = btoa(String.fromCharCode(...new Uint8Array(s16leBuffer)));
    
    // Calculate final metrics
    const avgAudioLevel = sum / audioData.length;
    const rms = Math.sqrt(sumSquares / audioData.length);

    // Get participant information for this audio stream
    const participantInfo = getParticipantForAudioStream(streamId);

    // Send to server
    const message = {
      type: 'audio_data',
      streamId: streamId,
      timestamp: Date.now(),
      sampleRate: sampleRate,
      data: s16leAudio,
      audioLevel: avgAudioLevel,
      rms: rms,
      clippingDetected: clippingDetected,
      sampleCount: audioData.length,
      // Add participant metadata if available
      participant: participantInfo ? {
        id: participantInfo.id,
        displayName: participantInfo.displayName,
        userId: participantInfo.userId,
        type: participantInfo.type
      } : null
    };

    // Send the message to server (only once)
    serverWebSocket?.send(JSON.stringify(message));

  } catch (error) {
    console.error('Error sending audio data to server:', error);
  }
}

function startStreamingToServer() {
  try {
    serverWebSocket = new WebSocket(serverUrl.value);

    serverWebSocket.onopen = () => {
      console.log('Connected to audio server');
      isStreamingToServer.value = true;
      streamingStatus.value = 'Connected to server';
      
      // After connecting, check if we have audio streams to process
      // If we're in a meeting and have audio streams, start processing them
      if (sessionStatus.value === 'JOINED') {
        console.log('Meeting is joined, checking for audio streams to process...');
        startAudioStreamProcessing();
      } else {
        console.log('Not in a meeting - audio streaming will start when you join a meeting');
        streamingStatus.value = 'Connected to server (waiting for meeting)';
      }
    };

    serverWebSocket.onmessage = (event) => {
      const message = JSON.parse(event.data);
      // Only log error messages or important status updates
      if (message.type === 'error') {
        console.error('Server error:', message);
      } else if (message.type === 'connection') {
        console.log('Server connection:', message.message);
      }
      // Don't log every audio_received message to reduce console spam
    };

    serverWebSocket.onclose = () => {
      console.log('Disconnected from audio server');
      isStreamingToServer.value = false;
      streamingStatus.value = 'Disconnected from server';
    };

    serverWebSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      isStreamingToServer.value = false;
      streamingStatus.value = 'Connection error';
    };

  } catch (error) {
    console.error('Error connecting to server:', error);
    streamingStatus.value = 'Connection failed';
  }
}

function stopStreamingToServer() {
  if (serverWebSocket) {
    serverWebSocket.close();
    serverWebSocket = null;
  }
  isStreamingToServer.value = false;
  streamingStatus.value = 'Not streaming';
}

function stopAudioStreamProcessing() {
  console.log('Stopping audio stream processing...');

  // Stop all audio stream processors
  for (const [streamId, processor] of audioStreamProcessors.entries()) {
    console.log(`Stopping audio processing for ${streamId}`);
    
    // Clean up ScriptProcessorNode resources
    if (processor.audioProcessor) {
      processor.audioProcessor.disconnect();
    }
    
    if (processor.source) {
      processor.source.disconnect();
    }
    
    if (processor.audioContext) {
      processor.audioContext.close();
    }
  }
  
  // Clear all processors
  audioStreamProcessors.clear();

  stopStreamingToServer();
  streamingStatus.value = 'Stopped';
}

// Login/Logout button click handler
function loginButtonClick() {
  console.log('Login/Logout button clicked!');
  console.log('isLoggedIn.value:', isLoggedIn.value);
  
  if (isLoggedIn.value) {
    console.log('Calling logout...');
    logout();
  } else {
    console.log('Calling login...');
    login();
  }
}

// Join/Leave Meeting button click handler
async function joinLeaveButtonClick() {
  console.log('Join/Leave Meeting button clicked!');
  console.log('sessionStatus.value:', sessionStatus.value);
  console.log('canJoinMeeting.value:', canJoinMeeting.value);
  
  try {
  if (sessionStatus.value === 'JOINED') {
    console.log('Calling leaveMeeting...');
    await leaveMeeting();
  } else {
    console.log('Calling joinMeeting...');
    await joinMeeting();
    }
  } catch (error: any) {
    console.error('Error in joinLeaveButtonClick:', error);
    // Don't re-throw the error - it's already handled in joinMeeting/leaveMeeting
    // Just log it and let the UI handle the error state
  }
}

// Google OAuth login
function login() {
  console.log('Login function called!');
  console.log('clientId.value:', clientId.value);
  
  if (!clientId.value) {
    alert("Please input a google cloud client id before logging in");
    return;
  }

  console.log('About to load Google OAuth script...');
  
  // Load Google OAuth script if not already loaded
  if (!(window as any).google) {
    console.log('Google script not loaded, loading it now...');
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      console.log('Google script loaded, calling performLogin...');
      performLogin();
    };
    script.onerror = (error) => {
      console.error('Failed to load Google script:', error);
    };
    document.head.appendChild(script);
  } else {
    console.log('Google script already loaded, calling performLogin...');
    performLogin();
  }
}

function performLogin() {
  console.log('performLogin function called!');
  console.log('window.google exists:', !!(window as any).google);
  console.log('window.google.accounts exists:', !!(window as any).google?.accounts);
  console.log('window.google.accounts.oauth2 exists:', !!(window as any).google?.accounts?.oauth2);
  
  try {
    const googleClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: clientId.value,
      scope: 'https://www.googleapis.com/auth/meetings.space.created https://www.googleapis.com/auth/meetings.conference.media.readonly https://www.googleapis.com/auth/meetings.space.readonly https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/userinfo.email',
      callback: (response: any) => {
        console.log('OAuth callback triggered!');
        console.log('response');
        console.log(response);
        console.log('Full scope string:', response.scope);
        console.log('Scope length:', response.scope?.length);
        tokenResponse.value = response;
        
        // Get user info and set default participants
        getUserInfoAndSetDefaults(response.access_token);
        
        console.log('Login successful!');
      },
      error_callback: (errorResponse: any) => {
        console.log('OAuth error callback triggered!');
        console.log('error');
        console.log(errorResponse);
        alert('Login failed: ' + JSON.stringify(errorResponse));
      },
    });
    
    console.log('Google client created, requesting access token...');
    googleClient.requestAccessToken();
    console.log('requestAccessToken called');
  } catch (error) {
    console.error('Error in performLogin:', error);
    alert('Error initializing Google OAuth: ' + error);
  }
}

async function getUserInfoAndSetDefaults(accessToken: string) {
  console.log('Getting user info with access token:', accessToken ? 'Token exists' : 'No token');
  
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    console.log('Userinfo API response status:', response.status);
    
    if (response.ok) {
      const userInfo = await response.json();
      console.log('User info received:', userInfo);
      const userEmail = userInfo.email;
      
      if (userEmail) {
        // Set current user email for display
        currentUserEmail.value = userEmail;
        // Set default participants based on user's email
        setDefaultParticipants(userEmail);
      } else {
        console.error('No email found in user info');
      }
    } else {
      const errorText = await response.text();
      console.error('Userinfo API error:', response.status, errorText);
    }
  } catch (error) {
    console.error('Error getting user info:', error);
  }
}

function setDefaultParticipants(userEmail: string) {
  console.log('Setting default participants for email:', userEmail);
  
  // Create the second email by replacing @ituri.ai with @devicepros.net
  const secondEmail = userEmail.replace('@ituri.ai', '@devicepros.net');
  
  defaultParticipants.value = [userEmail, secondEmail];
  
  console.log('Default participants set:', defaultParticipants.value);
  console.log('defaultParticipants.value length:', defaultParticipants.value.length);
}

function copyMeetingLink() {
  const meetingLink = `https://meet.google.com/${meetingCode.value}`;
  navigator.clipboard.writeText(meetingLink).then(() => {
    console.log('Meeting link copied to clipboard!');
  }).catch((err) => {
    console.error('Failed to copy meeting link:', err);
    alert('Failed to copy meeting link');
  });
}

function handleMeetingCodePaste(event: ClipboardEvent) {
  // Get the pasted text
  const pastedText = event.clipboardData?.getData('text') || '';
  
  // Check if it's a Google Meet URL and extract the meeting code
  const meetUrlPattern = /https?:\/\/meet\.google\.com\/([a-z-]+)/i;
  const match = pastedText.match(meetUrlPattern);
  
  if (match && match[1]) {
    // Extract the meeting code from the URL
    const extractedCode = match[1];
    
    // Prevent the default paste behavior
    event.preventDefault();
    
    // Set the meeting code to the extracted value
    meetingCode.value = extractedCode;
    
    console.log(`Extracted meeting code from URL: ${extractedCode}`);
  }
  // If it's not a URL, let the default paste behavior continue
}

function resetDisconnectionState() {
  console.log('Forcing disconnection state to complete...');
  disconnectionComplete.value = true;
  console.log('Disconnection state reset - can rejoin now');
}

function testAudioProcessing() {
  console.log('=== Testing Audio Processing ===');
  
  // Check if we have any audio streams
  const audioElements = document.querySelectorAll('audio[id^="audio-"]');
  console.log(`Found ${audioElements.length} audio elements`);
  
  if (audioElements.length === 0) {
    console.log('No audio elements found - no meeting streams to test');
    console.log('Continuing with other audio tests...');
  } else {
    console.log('Found meeting audio streams - testing them...');
  }
  
  // Test each audio element (only if we have meeting streams)
  if (audioElements.length > 0) {
    for (let i = 0; i < audioElements.length; i++) {
      const audioElement = audioElements[i] as HTMLAudioElement;
      const streamId = audioElement.id;
      
      console.log(`\n--- Testing ${streamId} ---`);
      console.log('Element properties:', {
        srcObject: !!audioElement.srcObject,
        paused: audioElement.paused,
        muted: audioElement.muted,
        volume: audioElement.volume,
        readyState: audioElement.readyState,
        networkState: audioElement.networkState
      });
      
      if (audioElement.srcObject) {
        const mediaStream = audioElement.srcObject as MediaStream;
        const audioTracks = mediaStream.getAudioTracks();
        console.log(`Audio tracks: ${audioTracks.length}`);
        
        for (const track of audioTracks) {
          console.log('Track details:', {
            id: track.id,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            kind: track.kind
          });
        }
      }
      
      // Check if we have a processor for this stream
      if (audioStreamProcessors.has(streamId)) {
        const processor = audioStreamProcessors.get(streamId)!;
        console.log('Processor details:', {
          contextState: processor.audioContext?.state || 'undefined',
          sampleRate: processor.audioContext?.sampleRate || 'undefined',
          hasProcessor: !!processor.audioProcessor,
          hasSource: !!processor.source
        });
      } else {
        console.log('No processor found for this stream');
      }
    }
  }
  
  // Generate a test tone to verify audio processing works
  console.log('\n--- Generating Test Tone ---');
  try {
    const testContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = testContext.createOscillator();
    const gainNode = testContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(testContext.destination);
    
    oscillator.frequency.setValueAtTime(440, testContext.currentTime); // A4 note
    gainNode.gain.setValueAtTime(0.1, testContext.currentTime); // Low volume
    
    oscillator.start(testContext.currentTime);
    oscillator.stop(testContext.currentTime + 0.5); // Play for 0.5 seconds
    
    console.log('[TEST_TONE] Test tone generated successfully');
    
    // Clean up after test
    setTimeout(() => {
      testContext.close();
      console.log('[TEST_TONE] Test tone cleanup completed');
    }, 1000);
    
  } catch (error) {
    console.error('[TEST_TONE] Error generating test tone:', error);
  }
  
  // Test if audio processing callback mechanism works
  console.log('\n--- Testing Audio Processing Callback ---');
  try {
    const testContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = testContext.createOscillator();
    const gainNode = testContext.createGain();
    const processor = testContext.createScriptProcessor(4096, 1, 1);
    
    let callbackCount = 0;
    processor.onaudioprocess = (event) => {
      callbackCount++;
      if (callbackCount <= 5) {
        console.log(`[TEST_CALLBACK] Audio processing callback ${callbackCount} triggered`);
      }
    };
    
    oscillator.connect(gainNode);
    gainNode.connect(processor);
    processor.connect(testContext.destination);
    
    oscillator.frequency.setValueAtTime(440, testContext.currentTime);
    gainNode.gain.setValueAtTime(0.1, testContext.currentTime);
    
    oscillator.start(testContext.currentTime);
    oscillator.stop(testContext.currentTime + 1); // Play for 1 second
    
    console.log('Audio processing callback test started');
    
    // Check callback count after a delay
    setTimeout(() => {
      console.log(`[TEST_CALLBACK] Total callbacks triggered: ${callbackCount}`);
      testContext.close();
    }, 1500);
    
  } catch (error) {
    console.error('Error testing audio processing callback:', error);
  }
  
    try {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          console.log('[TEST_MIC] Microphone access granted');
          console.log('[TEST_MIC] Stream tracks:', stream.getTracks().map(track => ({
            id: track.id,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            kind: track.kind,
            label: track.label
          })));
          
          // Test processing microphone audio
          const micContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          const micSource = micContext.createMediaStreamSource(stream);
          const micProcessor = micContext.createScriptProcessor(4096, 1, 1);
          
          let micCallbackCount = 0;
          micProcessor.onaudioprocess = (event) => {
            micCallbackCount++;
            if (micCallbackCount <= 3) {
              const inputData = event.inputBuffer.getChannelData(0);
              const firstSamples = [];
              for (let i = 0; i < Math.min(5, inputData.length); i++) {
                firstSamples.push(inputData[i].toFixed(6));
              }
              console.log(`[TEST_MIC] Callback ${micCallbackCount}, first 5 samples:`, firstSamples);
            }
          };
          
          micSource.connect(micProcessor);
          micProcessor.connect(micContext.destination);
          
          console.log('[TEST_MIC] Microphone audio processing started - speak to test');
          
          // Stop after 3 seconds
          setTimeout(() => {
            console.log(`[TEST_MIC] Total microphone callbacks: ${micCallbackCount}`);
            stream.getTracks().forEach(track => track.stop());
            micContext.close();
          }, 3000);
          
        })
        .catch(error => {
          console.error('[TEST_MIC] Microphone access denied:', error);
        });
    } catch (error) {
      console.error('[TEST_MIC] Error testing microphone:', error);
    }
}




function logout() {
  // Clear all authentication data
  tokenResponse.value = null;
  currentUserEmail.value = '';
  defaultParticipants.value = [];
  
  // Clear any existing client
  if (client.value) {
    client.value.leaveMeeting();
    client.value = null;
  }
  
  // Reset session status
  sessionStatus.value = 'New';
  
  // Stop any audio processing
  stopAudioStreamProcessing();
  
  // Reset all connection states
  disconnectionComplete.value = true;
  
  console.log('Logged out successfully');
}

onMounted(() => {
  // Initialize any necessary setup
  console.log('MeetingMenu component mounted');
  
  // Initialize disconnection state - allow joining if not currently in a meeting
  disconnectionComplete.value = true;
  
  // Initialize participant list display
  participantsList.value = '<div class="participant-item">Not connected to any meeting</div>';
  

});

onUnmounted(() => {
  // Cleanup
  stopAudioStreamProcessing();
  if (client.value) {
    client.value.leaveMeeting();
  }
});
</script>

<style scoped>
.meeting-menu-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.meeting-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #007acc;
  padding-bottom: 10px;
}

.meeting-menu-header h2 {
  margin: 0;
  color: #007acc;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.close-button:hover {
  background-color: #f0f0f0;
  border-radius: 4px;
  color: #333;
}

.meeting-menu-content {
  background: white;
  padding: 30px;
  border-radius: 8px;
  min-width: 500px;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.section h3 {
  margin-top: 0;
  color: #333;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #555;
}

.form-group input[type="text"],
.form-group input[type="email"] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-group input[type="checkbox"] {
  margin-right: 8px;
}

.range-input {
  width: 100%;
  margin: 10px 0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  margin-right: 10px;
  margin-bottom: 10px;
  transition: background-color 0.3s;
}

.btn-primary {
  background-color: #007acc;
  color: white;
}

.btn-primary:hover {
  background-color: #005a9e;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover {
  background-color: #1e7e34;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover {
  background-color: #c82333;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #6c757d !important;
  color: #fff !important;
}

.btn:disabled:hover {
  background-color: #6c757d !important;
  color: #fff !important;
}

.status-indicators {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.status-indicator {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.status-indicator.active {
  background-color: #d4edda;
  color: #155724;
  border-color: #c3e6cb;
}

.help-text {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #6c757d;
}

.help-text small {
  line-height: 1.4;
}

.button-group {
  display: flex;
  gap: 10px;
  margin: 15px 0;
}

.status {
  font-weight: 600;
  color: #007acc;
  margin: 10px 0;
}

.info-box {
  background: #e9ecef;
  padding: 15px;
  border-radius: 4px;
  margin: 15px 0;
}

.info-box h4 {
  margin-top: 0;
  color: #495057;
}

.info-box ul {
  margin: 10px 0;
  padding-left: 20px;
}

.info-box li {
  margin-bottom: 5px;
  color: #6c757d;
}

.audio-section {
  border-color: #007acc;
  background: #f0f8ff;
}

.participants-list {
  margin-top: 10px;
}

.participant-item {
  margin: 5px 0;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007acc;
}

.result-box {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  font-family: monospace;
  font-size: 12px;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
}

.video-container {
  position: absolute;
  top: -9999px;
  left: -9999px;
}

.meeting-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.meeting-link-text {
  flex: 1;
  color: #007acc;
  text-decoration: none;
  word-break: break-all;
}

.meeting-link-text:hover {
  text-decoration: underline;
}

.copy-btn {
  padding: 4px 8px;
  font-size: 12px;
  margin: 0;
}

.meeting-code-input-group {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.meeting-code-input-group input {
  flex: 1;
}

.meeting-code-input-group .btn {
  margin: 0;
  white-space: nowrap;
}

.join-status-container {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.join-status-container .btn {
  margin: 0;
}

.session-status {
  font-weight: 600;
  color: #007acc;
  padding: 10px 20px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1;
  margin: 0;
}

.warning-box {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 15px;
  margin: 15px 0;
  color: #856404;
}

.warning-box strong {
  color: #856404;
}

.warning-box p {
  margin: 8px 0;
}

.warning-box ol {
  margin: 8px 0;
  padding-left: 20px;
}

.warning-box li {
  margin-bottom: 5px;
}

.user-info {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}
</style> 